import { isDefined } from '@kaoto/forms';
import { ElementModel, GraphElement, Node } from '@patternfly/react-topology';

import { PlaceholderType } from '../../../../models/placeholder.constants';
import { AddStepMode, IVisualizationNode } from '../../../../models/visualization/base-visual-entity';
import { IClipboardCopyObject } from '../../../../models/visualization/clipboard';
import { CamelComponentSchemaService } from '../../../../models/visualization/flows/support/camel-component-schema.service';
import { CamelRouteVisualEntityData } from '../../../../models/visualization/flows/support/camel-component-types';
import { EntitiesContextResult } from '../../../../providers/entities.provider';
import {
  IInteractionType,
  INodeInteractionAddonContext,
  IOnCopyAddon,
} from '../../../registers/interactions/node-interaction-addon.model';
import { CanvasNode } from '../../Canvas/canvas.models';
import { processOnCopyAddon } from '../ContextMenu/item-interaction-helper';
import { NoBendpointsEdge } from '../NoBendingEdge';

export const getNodeDragAndDropDirection = (
  draggedVizNode: IVisualizationNode,
  droppedVizNode: IVisualizationNode,
  droppedIntoEdge: boolean,
): 'forward' | 'backward' => {
  const isSameBaseEntity = draggedVizNode?.getId() === droppedVizNode?.getId();
  if (!isSameBaseEntity) return 'forward';

  const draggedNodeArray = (draggedVizNode.data.path ?? '').split('.');
  const droppedNodeArray = (droppedVizNode.data.path ?? '').split('.');
  for (let i = 0; i < Math.min(draggedNodeArray.length, droppedNodeArray.length); i++) {
    if (draggedNodeArray[i] !== droppedNodeArray[i]) {
      if (Number.isInteger(Number(draggedNodeArray[i])) && Number.isInteger(Number(droppedNodeArray[i]))) {
        return draggedNodeArray[i] < droppedNodeArray[i] ? 'forward' : 'backward';
      }
      break;
    }
  }

  // Special case: when a container sub-node is dragged backward and dropped on the container connected edge
  if (droppedIntoEdge) {
    return 'backward';
  }

  return 'forward';
};

export const handleValidNodeDrop = (
  draggedElement: GraphElement<ElementModel, CanvasNode['data']>,
  dropResult: GraphElement<ElementModel, unknown>,
  entitiesContext: EntitiesContextResult,
  nodeInteractionAddonContext: INodeInteractionAddonContext,
) => {
  const draggedVizNode = draggedElement.getData()?.vizNode;
  if (!isDefined(draggedVizNode)) return;

  let droppedVizNode: IVisualizationNode;
  let droppedIntoEdge = false;

  if (dropResult instanceof NoBendpointsEdge) {
    droppedVizNode = dropResult.getTarget().getData().vizNode;
    droppedIntoEdge = true;
  } else {
    droppedVizNode = (dropResult as Node).getData().vizNode;
  }

  let draggedNodeContent = draggedVizNode?.getCopiedContent();
  if (!isDefined(draggedNodeContent)) return;

  draggedNodeContent = processOnCopyAddon(
    draggedVizNode,
    draggedNodeContent,
    (vn) => nodeInteractionAddonContext.getRegisteredInteractionAddons(IInteractionType.ON_COPY, vn) as IOnCopyAddon[],
  );

  if (!isDefined(draggedNodeContent)) return;

  // Drop onto special placeholder
  const isSpecialChildPlaceholder =
    droppedVizNode.data?.isPlaceholder &&
    droppedVizNode.data?.name !== PlaceholderType.Placeholder &&
    droppedVizNode.getParentNode()?.getNodeInteraction().canHaveSpecialChildren;
  if (isSpecialChildPlaceholder) {
    const parentVizNode = droppedVizNode.getParentNode();
    if (!parentVizNode) return;

    parentVizNode.pasteBaseEntityStep(draggedNodeContent, AddStepMode.InsertSpecialChildStep, true);
    draggedVizNode.removeChild();
  } else {
    /** Handle the drag and drop operation based on the direction differently:
        for forward direction we append the step to the dropped node, then remove the dragged node
        for backward direction we remove the dragged node first, then prepend the step to the dropped node
    */
    switch (getNodeDragAndDropDirection(draggedVizNode, droppedVizNode, droppedIntoEdge)) {
      case 'forward': {
        performForwardDrop(droppedVizNode, draggedVizNode, draggedNodeContent, droppedIntoEdge, entitiesContext);
        break;
      }
      case 'backward':
        draggedVizNode.removeChild();
        droppedVizNode.pasteBaseEntityStep(draggedNodeContent, AddStepMode.PrependStep);
        break;
    }
  }
  // Set an empty model to clear the graph
  draggedElement.getController().fromModel({
    nodes: [],
    edges: [],
  });
  requestAnimationFrame(() => {
    entitiesContext.updateEntitiesFromCamelResource();
  });
};

export const checkNodeDropCompatibility = (
  draggedVizNode: IVisualizationNode,
  droppedVizNode: IVisualizationNode,
  validate: (mode: AddStepMode, filterNode: IVisualizationNode, compatibilityCheckNodeName: string) => boolean,
): boolean => {
  const droppedVizNodeContent = draggedVizNode.getCopiedContent();
  const targetVizNodeContent = droppedVizNode.getCopiedContent();
  if (!isDefined(droppedVizNodeContent) || !isDefined(targetVizNodeContent)) return false;

  // prevent dragged container sub-nodes and sub-containers as dropping targets
  const draggedPath = draggedVizNode.data.path;
  const droppedPath = droppedVizNode.data.path;
  if (
    isDefined(draggedPath) &&
    droppedPath?.startsWith(draggedPath + '.') &&
    droppedVizNode.getId() === draggedVizNode.getId()
  )
    return false;

  // validation for placeholder nodes
  if (droppedVizNode.data.isPlaceholder) {
    if (droppedVizNode.data.name === draggedVizNode?.data.name) {
      const draggedParent = draggedVizNode.getParentNode();
      const droppedParent = droppedVizNode.getParentNode();
      return !(draggedParent?.id === droppedParent?.id && draggedParent?.getId() === droppedParent?.getId());
    }

    if (
      droppedVizNode.data.name === PlaceholderType.Placeholder &&
      droppedVizNode.getPreviousNode() !== draggedVizNode
    ) {
      return validate(AddStepMode.ReplaceStep, droppedVizNode, droppedVizNodeContent.name);
    }

    return false;
  }

  // validation for special children nodes in case of Route Entity
  if (droppedVizNodeContent.name !== targetVizNodeContent.name) return false;
  const targetVizNodeParent = droppedVizNode.getParentNode();
  if (targetVizNodeParent?.getNodeInteraction().canHaveSpecialChildren) {
    return validate(AddStepMode.InsertSpecialChildStep, targetVizNodeParent, droppedVizNodeContent.name);
  }

  return false;
};

const performForwardDrop = (
  droppedVizNode: IVisualizationNode,
  draggedVizNode: IVisualizationNode,
  draggedNodeContent: IClipboardCopyObject,
  droppedIntoEdge: boolean,
  entitiesContext: EntitiesContextResult,
) => {
  droppedVizNode.pasteBaseEntityStep(
    draggedNodeContent,
    droppedIntoEdge ? AddStepMode.PrependStep : AddStepMode.AppendStep,
  );
  const draggedVizNodeinteraction = draggedVizNode.getNodeInteraction();
  if (draggedVizNodeinteraction.canRemoveStep) {
    draggedVizNode.removeChild();
  } else if (draggedVizNodeinteraction.canRemoveFlow) {
    const flowId = draggedVizNode?.getId();
    entitiesContext.camelResource.removeEntity(flowId ? [flowId] : undefined);
  }
};

export interface VizNodeChildrenInfo {
  childCount: number;
  hasGroupChildren: boolean;
  isCollapsedGroup: boolean;
}

export const getVizNodeChildrenInfo = (vizNode: IVisualizationNode | undefined): VizNodeChildrenInfo => {
  const processorName = (vizNode?.data as CamelRouteVisualEntityData)?.processorName;
  const vizNodeChildren = vizNode?.getChildren() ?? [];
  const childCount = vizNodeChildren.filter((c) => !c.data.isPlaceholder).length;
  const hasGroupChildren = vizNodeChildren.some((child) => {
    const childProcessorName = (child.data as CamelRouteVisualEntityData)?.processorName;
    return childProcessorName
      ? CamelComponentSchemaService.getProcessorStepsProperties(childProcessorName).length > 0
      : false;
  });
  const isContainerType =
    !!processorName && CamelComponentSchemaService.getProcessorStepsProperties(processorName).length > 0;
  const isCollapsedGroup = isContainerType && childCount > 0;
  return { childCount, hasGroupChildren, isCollapsedGroup };
};
